import { useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import type { Coordinates } from 'tripPlanner/utils/chatItinerary'
import useAddHotelToTrip from './useAddHotelToTrip'
import type { SendMessageOptions } from './useAddHotelToTrip'
import AddHotelToTripModal from './AddHotelToTripModal'
import noop from 'lib/function/noop'

interface Props {
  itineraryData?: TripItineraryData
  hotelName: string
  offerId: string
  offerType?: string
  hotelCity?: string
  hotelCoordinates?: Coordinates
  contextStop?: TripStop
  onSendMessage: (message: string, options?: SendMessageOptions) => void
  variant?: 'filled' | 'outline'
  size?: 'small' | 'medium'
  onAfterAction?: () => void
  filterNights?: number
}

function AddHotelToTripButton({
  itineraryData,
  hotelName,
  offerId,
  offerType,
  hotelCity,
  hotelCoordinates,
  contextStop,
  onSendMessage,
  variant = 'filled',
  size = 'small',
  onAfterAction = noop,
  filterNights,
}: Props) {
  const intl = useIntl()
  const {
    handleAddToTrip,
    isModalOpen,
    closeModal,
    itineraryStatus,
    relevantStop,
    defaultDayNumber,
    defaultNights,
  } = useAddHotelToTrip({
    itineraryData,
    hotelName,
    offerId,
    offerType,
    hotelCity,
    hotelCoordinates,
    contextStop,
    onSendMessage,
    onAfterAction,
    filterNights,
  })

  const isAdded = itineraryStatus.type !== 'not-added'

  const addToTripLabel = intl.formatMessage({
    id: '07gQyyyv2TNz8wLL1Xgq',
    defaultMessage: 'Add to trip',
    description: 'Button to add a hotel to the trip itinerary in the trip planner',
  })

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    handleAddToTrip()
  }, [handleAddToTrip])

  const handleModalSubmit = useCallback((message: string) => {
    onSendMessage(message, { offerId, offerType })
    closeModal()
    onAfterAction()
  }, [onSendMessage, offerId, offerType, closeModal, onAfterAction])

  return (
    <>
      <AssistChip
        variant={variant}
        size={size}
        startIcon={!isAdded ? <LinePlusIcon /> : undefined}
        endIcon={isAdded ? <LineAngleDownIcon /> : undefined}
        onClick={handleClick}
      >
        {isAdded ? itineraryStatus.label : addToTripLabel}
      </AssistChip>
      {itineraryData && (
        <AddHotelToTripModal
          isOpen={isModalOpen}
          hotelName={hotelName}
          itineraryData={itineraryData}
          relevantStop={relevantStop}
          defaultDayNumber={defaultDayNumber}
          defaultNights={defaultNights}
          onClose={closeModal}
          onSubmit={handleModalSubmit}
        />
      )}
    </>
  )
}

export default AddHotelToTripButton
